import react, { useState, useEffect, useContext } from "react";
import ActionsContext from "common/ActionsContext";
import styled from "styled-components";
import * as d3 from "d3";
import {
  getPolicyFilterCmd,
  getSubscriberGroupFilterCmd,
  getCategoryFilterCmd,
  getSubscriberFilterCmd,
  parseTimeEvolution,
} from "./api";
import Request from "common/Request";
import BoxplotMultiple from "common/BoxplotMultiple";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import LineChartOrdinal from "common/graphs/LineChartOrdinal";

const ChartTitle = styled.h4`
  margin-top: 0;
  margin-bottom: 0;
`;

const SelectWrapper = styled.div`
  width: 50%;
  max-width: 200px;
  margin: 0 10px 10px 0;
`;

const intervalForHours = (hours, interval = 60) =>
  hours === 24 * 7 ? 360 : hours === 24 * 30 ? 1440 : interval;

const HOURS_INTERVAL_MAP = {
  24: 60,
  168: 360,
  720: 1440,
  2160: 4320,
};

const retrieval = ({
  hours,
  subscriberGroups,
  policiesRate,
  category,
  target,
}) => {
  return ifCl
    .run(
      `show statistics dpi app-metrics hours ${hours} interval ${HOURS_INTERVAL_MAP[hours]} category ${getCategoryFilterCmd(
        category
      )} subscriber ${getSubscriberFilterCmd(
        target
      )} policy ${getPolicyFilterCmd(
        policiesRate
      )} subscriber-group ${getSubscriberGroupFilterCmd(subscriberGroups)}`,
      { timeout: DPI_STATS_TMOUT_MS }
    )
    .then((response) => parseTimeEvolution(response))
    .catch((error) => {
      showModalError("Error", error);
    });
};

function TimeCharts({
  plotMode,
  hours,
  subscriberGroups,
  policiesRate,
  display,
  data,
  showMinMax,
  target,
  className,
}) {
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);
  const title = `Time Evolution of ${
    display === "downall" ? "Downlink" : "Uplink"
  } speed per flow`;
  const selectedData = display === "downall" ? data.downlink : data.uplink;
  const apps = selectedData.map((item) => item.app);
  const [selectedApp, setSelectedApp] = useState(
    apps.length > 0 ? apps[0] : undefined
  );

  useEffect(() => {
    doLoad();
  }, [
    selectedApp,
  ]);

  function doLoad() {
    if (!selectedApp) {
      setRequest(null);
      return;
    }
    const doRetrieval = retrieval({
      hours,
      subscriberGroups,
      policiesRate,
      category: selectedApp,
      target,
    });
    setRequest(doRetrieval);
  }

  if (request === null) {
    return null;
  }
  return (
    <div className={className}>
      <ChartTitle>{`${title} for ${selectedApp}`}</ChartTitle>
      <SelectWrapper>
        <SelectInputWithIcon
          title="Application"
          name="application"
          icon="memory"
          selected={selectedApp}
          onChange={({ target }) => {
            setSelectedApp(target.value);
          }}
          options={apps.map((name) => {
            return { label: name, value: name };
          })}
        />
      </SelectWrapper>
      <Request during={request}>
        {(result) => {
          const data = display === "downall" ? result.downlink : result.uplink;
          return (
            <div>
              {plotMode === "boxplot" ? (
                <>
                  <BoxplotMultiple
                    data={data}
                    xField="time"
                    formatXField={d3.timeFormat("%m/%d %H:%M")}
                    minmax={showMinMax}
                  />
                </>
              ) : (
                <LineChartOrdinal
                  data={data}
                  xField="time"
                  formatXField={d3.timeFormat("%m/%d %H:%M")}
                  minmax={showMinMax}
                />
              )}
            </div>
          );
        }}
      </Request>
    </div>
  );
}

export default TimeCharts;
